import { useState } from 'react';

import { Box, Button, Badge, Flex, Separator, Stack, Text } from '@chakra-ui/react';
import { Building, Briefcase, Calendar, Chat, Clock, User } from '@phosphor-icons/react';

import { AxiosError } from 'axios';

import { fetchAPI } from '@/api/fetch';
import { apiCreateScreening, apiUpdateScreening } from '@/api/services/screening.service';
import { useCreateInterviewAgent } from '@/store/createInterviewAgent';
import { Screening } from '@/types/Screening';
import { calculateTimestamp } from '@/utils';
import { ScreeningType } from '@/types/enums';

interface UpdatedScreeningData {
  session_name: string;
  deadline: number;
  interview_title: string;
  interview_description: string;
  retake_limit: number;
  session_time_limit: number;
  questions: string[];
}

interface ReviewSectionProps {
  onUpdateSuccess?: (updatedScreening: UpdatedScreeningData) => void;
}

export function ReviewSection({ onUpdateSuccess }: ReviewSectionProps = {}) {
  const [isError, setIsError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    screeningType,
    deadline,
    deadlineTime,
    timezone,
    sessionName,
    client,
    job,
    candidate,
    interviewTitle,
    interviewDescription,
    retakeLimit,
    sessionTimeLimit,
    questions,
    prevStep,
    nextStep,
    isUpdateMode,
    screeningId
  } = useCreateInterviewAgent();

  const formatDateTime = () => {
    if (!deadline || !deadlineTime || !timezone) return 'Not set';

    const combinedDateTime = new Date(deadline);
    combinedDateTime.setHours(deadlineTime.getHours(), deadlineTime.getMinutes());

    return `${combinedDateTime.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    })} at ${deadlineTime.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })}`;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setIsError(null);

    try {
      if (
        (screeningType === ScreeningType.JobInterview && !job?.job_id) ||
        !candidate?.candidate_id ||
        !deadline ||
        !deadlineTime ||
        !timezone
      ) {
        throw new Error('Missing required fields');
      }

      const deadlineTimestamp = calculateTimestamp(deadline, deadlineTime, timezone.value);
      const timezoneCode = timezone.value.value;

      if (isUpdateMode && screeningId) {
        // Update existing screening
        const updatePayload = {
          session_name: sessionName !== '' ? sessionName : `Interview with ${candidate.first_name}`,
          deadline: deadlineTimestamp,
          timezone: timezoneCode,
          interview_title: interviewTitle || '',
          interview_description: interviewDescription || '',
          retake_limit: retakeLimit,
          session_time_limit: sessionTimeLimit,
          questions: questions.map((q) => q)
        };

        const response = await fetchAPI<void>({
          request: () => apiUpdateScreening({ screeningId, payload: updatePayload })
        });

        if (response.success) {
          // Create updated screening object for callback
          const updatedScreening = {
            screening_id: screeningId,
            session_name: updatePayload.session_name,
            deadline: deadlineTimestamp,
            interview_title: updatePayload.interview_title,
            interview_description: updatePayload.interview_description,
            retake_limit: updatePayload.retake_limit,
            session_time_limit: updatePayload.session_time_limit,
            questions: updatePayload.questions
          };

          if (onUpdateSuccess) {
            onUpdateSuccess(updatedScreening);
          } else {
            nextStep();
          }
        } else {
          console.error('Failed to update screening:', response.message);
          setIsError(response.message || 'Failed to update screening. Please try again.');
        }
      } else {
        // Create new screening
        const createPayload = {
          screening_type: screeningType,
          job_id: screeningType === ScreeningType.JobInterview ? job?.job_id : undefined,
          candidate_id: candidate.candidate_id,
          session_name: sessionName !== '' ? sessionName : `Interview with ${candidate.first_name}`,
          deadline: deadlineTimestamp,
          timezone: timezoneCode,
          interview_title: interviewTitle || '',
          interview_description: interviewDescription || '',
          retake_limit: retakeLimit,
          session_time_limit: sessionTimeLimit,
          questions
        };

        const response = await fetchAPI<Screening | AxiosError>({
          request: () => apiCreateScreening({ payload: createPayload })
        });

        if (response.success) {
          nextStep();
        } else {
          console.error('Failed to create screening:', response.message);
          setIsError(response.message || 'Failed to create interview agent. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error processing screening:', error);
      setIsError(
        error instanceof Error
          ? error.message
          : `Failed to ${isUpdateMode ? 'update' : 'create'} screening. Please try again.`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    prevStep();
  };

  return (
    <Box
      w="100%"
      border="1px solid"
      borderColor="neutrals.200"
      borderRadius="12px"
      p="32px"
      bg="background.secondary"
    >
      <Stack gap="32px">
        {/* Header */}
        <Stack gap="0" mb="16px">
          <Text fontSize="20px" color="neutrals.900" fontWeight="bold">
            Review Interview Setup
          </Text>
          <Text fontSize="16px" color="neutrals.500">
            Review details before {isUpdateMode ? 'updating' : 'scheduling'} the automated
            interview.
          </Text>
        </Stack>

        {/* Session Details Section */}
        <Box
          border="1px solid"
          borderColor="neutrals.200"
          borderRadius="12px"
          p="24px"
          bg="neutrals.50"
        >
          <Flex justifyContent="space-between" alignItems="center" mb="20px">
            <Text fontSize="18px" fontWeight="semibold" color="neutrals.900">
              Session Details
            </Text>
          </Flex>

          <Stack gap="16px">
            {/* Session Title */}
            {sessionName && (
              <Flex alignItems="center" gap="12px" color="neutrals.700">
                <Chat size={20} />
                <Box>
                  <Text fontSize="14px" color="neutrals.500" fontWeight="medium">
                    Session Name
                  </Text>
                  <Text fontSize="16px" color="neutrals.900">
                    {sessionName}
                  </Text>
                </Box>
              </Flex>
            )}

            {/* Deadline */}
            <Flex alignItems="center" gap="12px" color="neutrals.700">
              <Calendar size={20} />
              <Box>
                <Text fontSize="14px" color="neutrals.500" fontWeight="medium">
                  Deadline
                </Text>
                <Text fontSize="16px" color="neutrals.900">
                  {formatDateTime()} {timezone?.value.label || ''}
                </Text>
              </Box>
            </Flex>

            {/* Client */}
            {client && (
              <Flex alignItems="center" gap="12px" color="neutrals.700">
                <Building size={20} />
                <Box>
                  <Text fontSize="14px" color="neutrals.500" fontWeight="medium">
                    Client
                  </Text>
                  <Text fontSize="16px" color="neutrals.900">
                    {client.unique_id} - {client.name}
                  </Text>
                </Box>
              </Flex>
            )}

            {/* Job */}
            {job && (
              <Flex alignItems="center" gap="12px" color="neutrals.700">
                <Briefcase size={20} />
                <Box>
                  <Text fontSize="14px" color="neutrals.500" fontWeight="medium">
                    Job Position
                  </Text>
                  <Text fontSize="16px" color="neutrals.900">
                    {job.unique_id} - {job.job_title}
                  </Text>
                </Box>
              </Flex>
            )}

            {/* Candidate */}
            {candidate && (
              <Flex alignItems="center" gap="12px" color="neutrals.700">
                <User size={20} />
                <Box>
                  <Text fontSize="14px" color="neutrals.500" fontWeight="medium">
                    Candidate
                  </Text>
                  <Text fontSize="16px" color="neutrals.900">
                    {candidate.unique_id} - {candidate.first_name} ({candidate.email})
                  </Text>
                </Box>
              </Flex>
            )}
          </Stack>
        </Box>

        {/* Interview Configuration Section */}
        <Box
          border="1px solid"
          borderColor="neutrals.200"
          borderRadius="12px"
          p="24px"
          bg="neutrals.50"
        >
          <Flex justifyContent="space-between" alignItems="center" mb="20px">
            <Text fontSize="18px" fontWeight="semibold" color="neutrals.900">
              Interview Configuration
            </Text>
          </Flex>

          <Stack gap="20px">
            {/* Interview Title */}
            <Box>
              <Text fontSize="14px" color="neutrals.500" fontWeight="medium" mb="8px">
                Interview Title
              </Text>
              <Text fontSize="16px" color="neutrals.900">
                {interviewTitle || 'Not specified'}
              </Text>
            </Box>

            {/* Interview Settings */}
            <Flex gap="32px">
              <Box>
                <Text fontSize="14px" color="neutrals.500" fontWeight="medium" mb="8px">
                  Retake Limit
                </Text>
                <Badge
                  bg="primary.100"
                  color="primary.800"
                  fontSize="14px"
                  px="12px"
                  py="4px"
                  borderRadius="8px"
                >
                  {retakeLimit === -1
                    ? 'Unlimited'
                    : `${retakeLimit} ${retakeLimit === 1 ? 'attempt' : 'attempts'}`}
                </Badge>
              </Box>

              <Box>
                <Text fontSize="14px" color="neutrals.500" fontWeight="medium" mb="8px">
                  Session Time Limit
                </Text>
                <Flex alignItems="center" gap="8px">
                  <Clock size={16} color="var(--chakra-colors-neutrals-600)" />
                  <Text fontSize="16px" color="neutrals.900">
                    {sessionTimeLimit} minutes
                  </Text>
                </Flex>
              </Box>
            </Flex>

            {/* Interview Description */}
            {interviewDescription && (
              <Box>
                <Text fontSize="14px" color="neutrals.500" fontWeight="medium" mb="8px">
                  Interview Description
                </Text>
                <Box
                  p="12px 16px"
                  bg="neutrals.0"
                  border="1px solid"
                  borderColor="neutrals.200"
                  borderRadius="8px"
                >
                  <Text fontSize="16px" color="neutrals.900" whiteSpace="pre-wrap">
                    {interviewDescription}
                  </Text>
                </Box>
              </Box>
            )}

            {/* Interview Questions */}
            <Box>
              <Flex justifyContent="space-between" alignItems="center" mb="12px">
                <Text fontSize="14px" color="neutrals.500" fontWeight="medium">
                  Interview Questions
                </Text>
                <Badge
                  bg="primary.100"
                  color="secondary.800"
                  fontSize="14px"
                  px="12px"
                  py="4px"
                  borderRadius="8px"
                >
                  {questions.length} {questions.length === 1 ? 'question' : 'questions'}
                </Badge>
              </Flex>

              <Stack gap="12px">
                {questions.length === 0 ? (
                  <Box
                    p="12px 16px"
                    bg="neutrals.0"
                    border="1px solid"
                    borderColor="neutrals.200"
                    borderRadius="8px"
                    textAlign="center"
                  >
                    <Text fontSize="16px" color="neutrals.500">
                      No questions added yet
                    </Text>
                  </Box>
                ) : (
                  questions.map((question, index) => (
                    <Box
                      key={index}
                      p="12px 16px"
                      bg="neutrals.0"
                      border="1px solid"
                      borderColor="neutrals.200"
                      borderRadius="8px"
                    >
                      <Flex gap="12px">
                        <Text fontSize="14px" fontWeight="medium" color="neutrals.700" minW="20px">
                          {index + 1}.
                        </Text>
                        <Text fontSize="16px" color="neutrals.900">
                          {question}
                        </Text>
                      </Flex>
                    </Box>
                  ))
                )}
              </Stack>
            </Box>
          </Stack>
        </Box>

        {/* Error Message */}
        {isError && (
          <Box color="error.700" fontSize="16px" role="alert">
            {isError}
          </Box>
        )}

        {/* Action Buttons */}
        <Separator />
        <Flex justifyContent="flex-end" gap="16px">
          <Button
            p="12px 64px"
            fontSize="16px"
            border="1px solid"
            borderColor="primary.900"
            borderRadius="8px"
            bg="neutrals.0"
            color="primary.900"
            disabled={isSubmitting}
            onClick={handleBack}
            _hover={{
              bg: 'neutrals.50'
            }}
          >
            Back
          </Button>
          <Button
            p="12px 50px"
            fontSize="16px"
            borderRadius="8px"
            bg="primary.900"
            color="neutrals.0"
            loading={isSubmitting}
            onClick={handleSubmit}
            _hover={{
              bg: 'primary.700'
            }}
          >
            {isUpdateMode ? 'Update' : 'Create'}
          </Button>
        </Flex>
      </Stack>
    </Box>
  );
}
